import { Sticky } from '@/config/sticky-layout';
import { Box, Button, Flex, Table, Tbody, Text, Thead, Tr } from '@chakra-ui/react';
import { TableEmptyRows, TableHeader, TableSkeletonRows } from '@sphere/ui';
import useTranslation from 'next-translate/useTranslation';
import { rem } from 'polished';
import { FC } from 'react';
import { ActivityItemDesktop, ActivityItemMobile } from './ActivityItem';
import { ActivityResponse } from './utils';

type Props = {
  activityResponse: ActivityResponse;
  hideItemColumn?: boolean;
};

export const ActivityTable: FC<Props> = ({ activityResponse, hideItemColumn }) => {
  const { t } = useTranslation('activity');

  const { data, fetchNextPage, hasNextPage, isInitialLoading, isFetchingNextPage, isLoading } =
    activityResponse;

  const hideTransactionColumn = !data.some(activity => activity.txHash);
  const hideToAddressColumn = !data.some(activity => activity.toAddress);

  return (
    <>
      <Table
        variant="unstyled"
        position="relative"
        display={{ base: 'none', '2xl': 'table' }}
        style={{ borderCollapse: 'separate', borderSpacing: `0 ${rem(8)}` }}
      >
        <Sticky
          as={Thead}
          element="table-header"
          zIndex="sticky-layout-contained"
          stuck={{
            ...(data.length > 1 && {
              animate: {
                boxShadow: 'sticky.table-header',
              },
            }),
          }}
        >
          <Tr _hover={{}} borderBottom="none">
            {!hideItemColumn && <TableHeader pl={0}>{t('table.headers.item.label')}</TableHeader>}
            <TableHeader>{t('table.headers.event.label')}</TableHeader>
            <TableHeader textAlign="right">{t('table.headers.total-amount.label')}</TableHeader>
            <TableHeader>{t('table.headers.from.label')}</TableHeader>
            {!hideToAddressColumn && <TableHeader>{t('table.headers.to.label')}</TableHeader>}
            <TableHeader>{t('table.headers.time.label')}</TableHeader>
            {!hideTransactionColumn && (
              <TableHeader textAlign="right">{t('table.headers.transaction.label')}</TableHeader>
            )}
          </Tr>
        </Sticky>
        <Tbody>
          {isLoading ? (
            <TableSkeletonRows rows={20} cols={7} height={rem(80)} />
          ) : (
            data.map((activity, index) => (
              <ActivityItemDesktop
                key={index + (activity.token?.tokenId || '')}
                hideItemColumn={hideItemColumn}
                hideTransactionColumn={hideTransactionColumn}
                hideToAddressColumn={hideToAddressColumn}
                {...activity}
              />
            ))
          )}
        </Tbody>
      </Table>

      <Box display={{ '2xl': 'none' }}>
        {isLoading ? (
          <TableSkeletonRows rows={20} height={{ base: rem(185), sm: rem(135) }} isMobile />
        ) : (
          data.map((activity, index) => (
            <ActivityItemMobile
              key={index + (activity?.token?.tokenId || '')}
              hideItemColumn={hideItemColumn}
              {...activity}
            />
          ))
        )}
      </Box>

      {!isInitialLoading && !data.length && (
        <>
          <Text textAlign="center" fontSize="md" color="gray.5" my="space.32">
            {t('empty.label')}
          </Text>
          <TableEmptyRows />
        </>
      )}

      {hasNextPage && (
        <Flex justifyContent="center">
          <Button
            mt="space.16"
            variant="secondary"
            onClick={() => fetchNextPage()}
            isLoading={isFetchingNextPage}
          >
            {t('table.load-more')}
          </Button>
        </Flex>
      )}
    </>
  );
};
